import { createFileRoute, Link } from "@tanstack/react-router";
import { useFleetSidebar } from "@/features/brewcontrol/use-fleet-sidebar";
import { formatDistanceToNow } from "date-fns";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { Button } from "@/components/ui/button";
import {
  ConnectionDot,
  LifecycleBadge,
  type NodeLifecycle,
} from "@/features/brewcontrol/node-status";

export const Route = createFileRoute("/brewcontrol/")({
  component: FleetPage,
});

/**
 * The fleet. One row per node, every column answering a question an operator
 * actually asks before touching anything: is it up, is it trusted, what is it
 * running, is anything forced, is any device unhealthy.
 */
function FleetPage() {
  useFleetSidebar();
  const canManage = useCan("brewcontrol-nodes:manage");
  const { data: nodes, isLoading } = trpc.brewcontrol.listNodes.useQuery(
    undefined,
    { refetchInterval: 5000 },
  );

  const { data: archived = [] } = trpc.brewcontrol.listArchivedNodes.useQuery();

  return (
    <div className="flex-1 overflow-auto">
      <div className="px-6 py-6 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-sm font-medium">Node fleet</h1>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Nodes dial BrewBase and hold the connection open. BrewBase never
              connects out, so a node that is not listed as connected can only be
              fixed at the node.
            </p>
          </div>
          {canManage && (
            <Button asChild>
              <Link to="/brewcontrol/enrol">Enrol node</Link>
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="border border-dashed p-4 text-center text-sm text-muted-foreground">
            Loading fleet…
          </div>
        ) : (nodes?.length ?? 0) === 0 ? (
          <div className="border border-dashed p-4 text-center text-sm text-muted-foreground">
            No nodes enrolled yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-olive-11">
                <th className="w-56 px-2 py-1.5 text-left font-medium">Node</th>
                <th className="w-36 px-2 py-1.5 text-left font-medium">Status</th>
                <th className="w-48 px-2 py-1.5 text-left font-medium">Running</th>
                <th className="w-28 px-2 py-1.5 text-right font-medium">Devices</th>
                <th className="w-28 px-2 py-1.5 text-right font-medium">Forced</th>
                <th className="w-32 px-2 py-1.5 text-left font-medium">Version</th>
                <th className="px-2 py-1.5 text-left font-medium">Uptime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-olive-5">
              {nodes?.map((node) => (
                <tr key={node.id} className="hover:bg-olive-2">
                  <td className="px-2 py-1.5">
                    <Link
                      to="/brewcontrol/$nodeId"
                      params={{ nodeId: node.id }}
                      className="flex items-center gap-2 hover:underline"
                    >
                      <ConnectionDot connected={node.connected} />
                      <span className="truncate">{node.display_name}</span>
                    </Link>
                    {node.hostname && (
                      <div className="pl-3.5 text-xs text-muted-foreground">
                        {node.hostname}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <LifecycleBadge status={node.status as NodeLifecycle} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    {node.activeBcRunId ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {node.connected ? (
                      <>
                        {node.deviceCount}
                        {node.unhealthyDeviceCount > 0 && (
                          <span className="ml-1 text-red-11">
                            ({node.unhealthyDeviceCount} bad)
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {node.overrideCount > 0 ? (
                      <span className="text-amber-11">{node.overrideCount}</span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    {node.agent_version ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">
                    {node.connected && node.connectedAt
                      ? `connected ${formatDistanceToNow(node.connectedAt, { addSuffix: true })}`
                      : node.last_disconnected_at
                        ? `last seen ${formatDistanceToNow(node.last_disconnected_at, { addSuffix: true })}`
                        : "never connected"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {archived.length > 0 && (
          <section className="space-y-1">
            <h2 className="text-sm font-medium">Archived</h2>
            <div className="divide-y divide-olive-5 border-t">
              {archived.map((node) => (
                <Link
                  key={node.id}
                  to="/brewcontrol/$nodeId"
                  params={{ nodeId: node.id }}
                  className="flex gap-4 px-2 py-1.5 text-sm text-olive-11 hover:bg-olive-2"
                >
                  <span className="w-56 truncate">{node.display_name}</span>
                  <span className="w-48 truncate font-mono text-xs">
                    {node.hostname ?? "—"}
                  </span>
                  <span className="text-xs">
                    archived{" "}
                    {node.deleted_at &&
                      formatDistanceToNow(node.deleted_at, { addSuffix: true })}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
